import React, { useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import FileUploader from '@/components/Admin/UI/FileUploader';
import {
   getIspPublicTenant,
   applyToIspTenant,
   type IspApplication,
} from '@/api/isp';
import {
   PublicReset,
   BrandHeader,
   BrandFooter,
   Shell,
   Body,
   Card,
   CardTitle,
   CardNote,
   Field,
   Lbl,
   Input,
   Area,
   Hint,
   Err,
   Primary,
   Fine,
} from './ispPublicUI';

/**
 * "I want internet" — the operator's public sign-up page.
 *
 * ─── FIXED FIELDS, NOT A FORM BUILDER ───
 * Ray: *"maybe fixed field."* Everybody is asked the same things. A form
 * builder would be a second product, and an operator who designs a bad form
 * loses customers without ever learning they applied.
 *
 * ─── THE PLAN IS PICKED HERE ───
 * Ray, 2026-09-01: *"the plans cant be select, so how the opeator knows which
 * plan customer choose."* Right — the plans were decoration. Choosing one is
 * a WISH, not a purchase: it is recorded on the application so the operator
 * knows what to quote, and they still pick the real plan when they build the
 * connection.
 *
 * ─── AND THE ID ───
 * Measured on staging 2026-09-01: an application arrived with no ID at all,
 * because this form never asked for one. The operator verifies identity before
 * putting somebody on their network, so they cannot decide without it.
 */
const peso = (n: number) => `₱${Number(n ?? 0).toLocaleString()}`;

const ID_TYPES = [
   'National ID',
   "Driver's licence",
   'Passport',
   'UMID / SSS',
   'PhilHealth',
   'Postal ID',
   'Voter’s ID',
   'Other',
];

const IspApply: React.FC = () => {
   const { slug = '' } = useParams();
   const {
      data: tenant,
      isLoading,
      isError,
   } = useQuery({
      queryKey: ['isp-public', slug],
      queryFn: () => getIspPublicTenant(slug),
      retry: false,
   });

   const [f, setF] = useState<IspApplication>({
      fullName: '',
      phone: '',
      address: '',
      email: '',
      notes: '',
      plan: '',
      idType: '',
      idPhotoUrl: '',
   });
   const [err, setErr] = useState('');
   const set =
      (k: keyof IspApplication) =>
      (
         e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
         >
      ) =>
         setF((old) => ({ ...old, [k]: e.target.value }));

   const apply = useMutation({
      mutationFn: () => applyToIspTenant(slug, f),
      onError: (e: unknown) =>
         setErr(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not send that. Please try again.'
         ),
   });

   if (isLoading)
      return (
         <Shell>
            <Body>
               <SkeletonLines lines={3} />
            </Body>
         </Shell>
      );

   if (isError || !tenant) {
      return (
         <Shell>
            <PublicReset />
            <Body>
               <Card>
                  <CardTitle>Page not found</CardTitle>
                  <CardNote>
                     This sign-up link is not working. Ask the provider for a new
                     one.
                  </CardNote>
               </Card>
            </Body>
         </Shell>
      );
   }

   const brand = {
      businessName: tenant.businessName,
      logoUrl: tenant.logoUrl,
      address: tenant.address,
      contactNumber: tenant.contactNumber,
   };

   /* ------------------------------------------------------- after they send */

   if (apply.isSuccess) {
      const chosen = tenant.plans.find((p) => p.id === f.plan);
      return (
         <Shell>
            <PublicReset />
            <BrandHeader brand={brand} />
            <Body>
               <DoneCard>
                  <Tick>✓</Tick>
                  <DoneTitle>Application received</DoneTitle>
                  <DoneLead>{apply.data.message}</DoneLead>

                  {/* A receipt of what they asked for. Somebody who mistyped
                      their own number finds out HERE, not in a week when
                      nobody has rung. */}
                  <Summary>
                     <SumRow>
                        <SumK>Name</SumK>
                        <SumV>{f.fullName}</SumV>
                     </SumRow>
                     <SumRow>
                        <SumK>Mobile</SumK>
                        <SumV>{f.phone}</SumV>
                     </SumRow>
                     <SumRow>
                        <SumK>Address</SumK>
                        <SumV>{f.address}</SumV>
                     </SumRow>
                     {chosen ? (
                        <SumRow>
                           <SumK>Plan</SumK>
                           <SumV>
                              {chosen.name} · {peso(chosen.price)}/month
                           </SumV>
                        </SumRow>
                     ) : null}
                  </Summary>

                  <Steps>
                     <StepsTitle>What happens next</StepsTitle>
                     <Step>
                        <StepNo>1</StepNo>
                        <StepText>
                           {tenant.businessName} checks your details and your
                           address is in range.
                        </StepText>
                     </Step>
                     <Step>
                        <StepNo>2</StepNo>
                        <StepText>
                           They call you on <strong>{f.phone}</strong> to agree a
                           date.
                        </StepText>
                     </Step>
                     <Step>
                        <StepNo>3</StepNo>
                        <StepText>
                           They install your connection and set up your bill.
                        </StepText>
                     </Step>
                  </Steps>

                  {tenant.contactNumber ? (
                     <CallBtn
                        href={`tel:${tenant.contactNumber.replace(/\s/g, '')}`}
                     >
                        Call {tenant.contactNumber}
                     </CallBtn>
                  ) : null}
                  <Fine>
                     You are not connected yet — nothing is charged until they
                     install it.
                  </Fine>
               </DoneCard>
            </Body>
            <BrandFooter brand={brand} />
         </Shell>
      );
   }

   /* -------------------------------------------------------------- the form */

   return (
      <Shell>
         <PublicReset />
         <BrandHeader brand={brand} subtitle="Apply for an internet connection" />
         <Body>
            {tenant.plans.length > 0 && (
               <Card>
                  <CardTitle>Choose a plan</CardTitle>
                  <CardNote>
                     You can change your mind when they call — this just tells
                     them what you are after.
                  </CardNote>
                  {tenant.plans.map((p) => (
                     <PlanRow
                        key={p.id}
                        type="button"
                        $on={f.plan === p.id}
                        onClick={() =>
                           setF((old) => ({
                              ...old,
                              plan: old.plan === p.id ? '' : p.id,
                           }))
                        }
                     >
                        <Radio $on={f.plan === p.id} />
                        <PlanMain>
                           <PlanName>{p.name}</PlanName>
                           {p.speedLabel ? (
                              <PlanSpeed>{p.speedLabel}</PlanSpeed>
                           ) : null}
                        </PlanMain>
                        <PlanPrice>
                           {peso(p.price)}
                           <PlanPer>/month</PlanPer>
                        </PlanPrice>
                     </PlanRow>
                  ))}
               </Card>
            )}

            <Card>
               <CardTitle>Your details</CardTitle>
               <form
                  onSubmit={(e) => {
                     e.preventDefault();
                     setErr('');
                     apply.mutate();
                  }}
               >
                  <Field>
                     <Lbl htmlFor="apply-name">Your full name</Lbl>
                     <Input
                        id="apply-name"
                        value={f.fullName}
                        onChange={set('fullName')}
                     />
                  </Field>
                  <Field>
                     <Lbl htmlFor="apply-phone">Mobile number</Lbl>
                     <Input
                        id="apply-phone"
                        value={f.phone}
                        onChange={set('phone')}
                        inputMode="tel"
                        placeholder="0917 555 0142"
                     />
                     <Hint>This is how they will reach you.</Hint>
                  </Field>
                  <Field>
                     <Lbl htmlFor="apply-address">
                        Where do you want the connection?
                     </Lbl>
                     <Input
                        id="apply-address"
                        value={f.address}
                        onChange={set('address')}
                        placeholder="House number, street, barangay"
                     />
                  </Field>
                  <Field>
                     <Lbl htmlFor="apply-email">Email (optional)</Lbl>
                     <Input
                        id="apply-email"
                        value={f.email}
                        onChange={set('email')}
                     />
                  </Field>

                  <Field>
                     <Lbl htmlFor="apply-idtype">Which ID will you show?</Lbl>
                     <IdSelect
                        id="apply-idtype"
                        value={f.idType}
                        onChange={set('idType')}
                     >
                        <option value="">Choose one</option>
                        {ID_TYPES.map((t) => (
                           <option key={t} value={t}>
                              {t}
                           </option>
                        ))}
                     </IdSelect>
                  </Field>
                  <Field>
                     <Lbl>Photo of your ID</Lbl>
                     <FileUploader
                        value={f.idPhotoUrl ?? ''}
                        onChange={(url) =>
                           setF((old) => ({ ...old, idPhotoUrl: url }))
                        }
                        accept="image/*,application/pdf"
                        compact
                     />
                     <Hint>
                        They check this before installing. It is not shown to
                        anyone else.
                     </Hint>
                  </Field>

                  <Field>
                     <Lbl htmlFor="apply-notes">Anything else? (optional)</Lbl>
                     <Area
                        id="apply-notes"
                        rows={3}
                        value={f.notes}
                        onChange={set('notes')}
                        placeholder="Best time to visit, landmark near your house…"
                     />
                  </Field>

                  {err ? <Err>{err}</Err> : null}

                  <Primary type="submit" disabled={apply.isPending}>
                     {apply.isPending ? 'Sending…' : 'Send my application'}
                  </Primary>
                  {/* Nobody should think they are connected because a form went
                      through. This is the sentence that prevents the phone
                      call. */}
                  <Fine>
                     Sending this does not connect you yet — {tenant.businessName}{' '}
                     will call you first.
                  </Fine>
               </form>
            </Card>
         </Body>
         <BrandFooter brand={brand} />
      </Shell>
   );
};

const PlanRow = styled.button<{ $on: boolean }>`
   width: 100%;
   display: flex;
   align-items: center;
   gap: 12px;
   text-align: left;
   font-family: inherit;
   color: inherit;
   padding: 13px 14px;
   margin-bottom: 9px;
   cursor: pointer;
   border-radius: 13px;
   background: transparent;
   border: 2px solid
      ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
`;
const Radio = styled.span<{ $on: boolean }>`
   width: 19px;
   height: 19px;
   border-radius: 50%;
   flex: none;
   border: 2px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $on }) =>
      $on
         ? `radial-gradient(${colors.PRIMARY} 0 45%, transparent 46%)`
         : 'transparent'};
`;
const PlanMain = styled.div`
   flex: 1;
`;
const PlanName = styled.div`
   font-size: 15px;
   font-weight: 700;
`;
const PlanSpeed = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const PlanPrice = styled.div`
   font-size: 16px;
   font-weight: 800;
   white-space: nowrap;
`;
const PlanPer = styled.span`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
`;
const IdSelect = styled.select`
   width: 100%;
   box-sizing: border-box;
   font-family: inherit;
   font-size: 16px;
   padding: 13px 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 11px;
   background: transparent;
   color: inherit;
`;
const DoneCard = styled(Card)`
   text-align: center;
   padding: 28px 20px;
`;
const Tick = styled.div`
   width: 58px;
   height: 58px;
   margin: 0 auto 14px;
   border-radius: 50%;
   background: rgba(25, 135, 84, 0.12);
   color: ${colors.GREEN};
   font-size: 30px;
   display: grid;
   place-items: center;
`;
const DoneTitle = styled.h1`
   margin: 0 0 6px;
   font-size: 21px;
`;
const DoneLead = styled.p`
   margin: 0 0 20px;
   font-size: 14px;
   line-height: 1.6;
   color: ${colors.GRAY5};
`;
const Summary = styled.div`
   text-align: left;
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   padding: 6px 14px;
   margin-bottom: 20px;
`;
const SumRow = styled.div`
   display: flex;
   justify-content: space-between;
   gap: 14px;
   padding: 9px 0;
   font-size: 13px;
   & + & {
      border-top: 1px solid ${colors.GRAY12};
   }
`;
const SumK = styled.span`
   color: ${colors.GRAY5};
   flex: none;
`;
const SumV = styled.span`
   font-weight: 700;
   text-align: right;
`;
const Steps = styled.div`
   text-align: left;
   margin-bottom: 20px;
`;
const StepsTitle = styled.div`
   font-size: 12px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: ${colors.GRAY5};
   margin-bottom: 10px;
`;
const Step = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 11px;
   margin-bottom: 11px;
`;
const StepNo = styled.div`
   width: 24px;
   height: 24px;
   border-radius: 50%;
   flex: none;
   background: ${colors.PRIMARY};
   color: #fff;
   font-size: 12px;
   font-weight: 800;
   display: grid;
   place-items: center;
`;
const StepText = styled.div`
   font-size: 13px;
   line-height: 1.55;
`;
const CallBtn = styled.a`
   display: block;
   text-align: center;
   font-size: 15px;
   font-weight: 800;
   padding: 14px;
   border-radius: 12px;
   border: 2px solid ${colors.PRIMARY};
   color: ${colors.PRIMARY};
   text-decoration: none;
`;

export default IspApply;
